/**
 * ProviderShowcaseLightbox — full-screen showcase that opens when a customer
 * clicks a vendor / pro / venue / entertainment-pro card on Instant Pricing.
 *
 * Aggregates ALL media the provider has uploaded:
 *   - portfolio_items[] (images + ≤30s videos)
 *   - decor_packages[].image + decor_packages[].media[]   (Decoration Team)
 *   - custom_packages[].images[]                           (Custom packages)
 *   - photos[]                                              (legacy thumbnails)
 *
 * Renders a sticky bottom action bar with:
 *   - effective price
 *   - "Add to estimate" / "Remove" button (delegated to parent via onToggleCart)
 *
 * NOT used for caterers, Chef / Biryani Specialist / Server / Catering Team
 * — those use the existing menu/team-pricing matrix flow.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, CaretLeft, CaretRight, Plus, MapPin, Star } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fullUrl = (p) => (p && p.startsWith('http') ? p : `${BACKEND_URL}${p || ''}`);

/** Build a flat slide list from a provider object. */
const slidesForProvider = (provider) => {
  if (!provider) return [];
  const slides = [];

  // 1. portfolio_items (newest universal field — used by venues, pros, ent-pros)
  (provider.portfolio_items || []).forEach((it) => {
    if (!it || !it.url) return;
    slides.push({
      kind: it.kind === 'video' ? 'video' : 'image',
      url: it.url,
      poster: it.poster || null,
      duration: it.duration || null,
      label: 'Portfolio',
    });
  });

  // 2. decor_packages — cover image + any extra media
  (provider.decor_packages || []).forEach((pkg) => {
    if (pkg?.image) {
      slides.push({ kind: 'image', url: pkg.image, poster: null, label: pkg.name || 'Decor Package' });
    }
    (pkg?.media || []).forEach((m) => {
      if (!m || !m.url) return;
      slides.push({
        kind: m.kind === 'video' ? 'video' : 'image',
        url: m.url,
        poster: m.poster || null,
        duration: m.duration || null,
        label: pkg?.name || 'Decor Package',
      });
    });
  });

  // 3. custom_packages — image arrays
  (provider.custom_packages || []).forEach((cp) => {
    (cp?.images || []).forEach((img) => {
      const url = typeof img === 'string' ? img : img?.url;
      if (!url) return;
      slides.push({ kind: 'image', url, poster: null, label: cp?.name || 'Custom Package' });
    });
  });

  // 4. legacy photos[]
  (provider.photos || []).forEach((p) => {
    const url = typeof p === 'string' ? p : p?.url;
    if (!url) return;
    slides.push({ kind: 'image', url, poster: null, label: provider.name || '' });
  });

  // De-dupe by url
  const seen = new Set();
  return slides.filter((s) => {
    if (seen.has(s.url)) return false;
    seen.add(s.url);
    return true;
  });
};

const ProviderShowcaseLightbox = ({
  open,
  provider,
  priceText,
  subPriceText,
  inCart,
  onToggleCart,
  onClose,
}) => {
  const [idx, setIdx] = useState(0);
  const videoRef = useRef(null);

  const slides = useMemo(() => slidesForProvider(provider), [provider]);

  useEffect(() => { if (open) setIdx(0); }, [open, provider?.id]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (slides.length > 1) {
        if (e.key === 'ArrowLeft') setIdx((i) => (i - 1 + slides.length) % slides.length);
        if (e.key === 'ArrowRight') setIdx((i) => (i + 1) % slides.length);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, slides.length]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  }, [idx]);

  if (!open || !provider) return null;
  const slide = slides[idx];
  const hasSlides = slides.length > 0;

  return (
    <div
      className="fixed inset-0 bg-black/95 z-[210] flex flex-col"
      data-testid="provider-showcase-lightbox"
      onClick={onClose}
    >
      {/* Top bar — close + provider name */}
      <div
        className="flex items-center justify-between px-4 py-3 text-sand-50 bg-gradient-to-b from-black/70 to-transparent"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0">
          <div className="font-heading text-base sm:text-lg font-bold truncate" data-testid="provider-showcase-name">
            {provider.business_name || provider.name}
          </div>
          <div className="flex items-center gap-2 text-[11px] text-sand-50/80 mt-0.5">
            {provider.rating != null && (
              <span className="flex items-center gap-0.5">
                <Star size={12} weight="fill" className="text-amber-400" />
                {provider.rating}{provider.reviews ? ` (${provider.reviews})` : ''}
              </span>
            )}
            {(provider.area || provider.location) && (
              <span className="flex items-center gap-0.5">
                <MapPin size={11} />
                {provider.area ? `${provider.area}, ${provider.location || ''}` : provider.location}
              </span>
            )}
            {provider.verified && (
              <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold bg-green-500/90 text-white rounded">
                Verified
              </span>
            )}
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          className="w-10 h-10 rounded-full bg-white/90 hover:bg-white text-text-primary flex items-center justify-center shadow flex-shrink-0 ml-3"
          aria-label="Close"
          data-testid="provider-showcase-close"
        >
          <X size={18} weight="bold" />
        </button>
      </div>

      {/* Media area */}
      <div
        className="flex-1 flex items-center justify-center relative px-2"
        onClick={(e) => e.stopPropagation()}
      >
        {hasSlides ? (
          slide?.kind === 'video' ? (
            <video
              ref={videoRef}
              src={fullUrl(slide.url)}
              poster={slide.poster ? fullUrl(slide.poster) : undefined}
              className="max-h-full max-w-full"
              controls
              autoPlay
              data-testid="provider-showcase-video"
            />
          ) : (
            <img
              src={fullUrl(slide.url)}
              alt={slide?.label || provider.name}
              className="max-h-full max-w-full object-contain"
              data-testid="provider-showcase-image"
            />
          )
        ) : (
          <div className="text-sand-50/60 text-center px-6">
            <div className="text-sm uppercase tracking-wider font-bold">No portfolio media yet</div>
            <div className="text-xs mt-1">This provider hasn&apos;t uploaded any photos or videos.</div>
          </div>
        )}

        {slides.length > 1 && (
          <>
            <button
              onClick={() => setIdx((i) => (i - 1 + slides.length) % slides.length)}
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow"
              aria-label="Previous"
              data-testid="provider-showcase-prev"
            >
              <CaretLeft size={20} weight="bold" />
            </button>
            <button
              onClick={() => setIdx((i) => (i + 1) % slides.length)}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 w-10 h-10 sm:w-12 sm:h-12 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow"
              aria-label="Next"
              data-testid="provider-showcase-next"
            >
              <CaretRight size={20} weight="bold" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/70 text-sand-50 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded">
              {idx + 1} / {slides.length}
              {slide?.label ? ` · ${slide.label}` : ''}
            </div>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {slides.length > 1 && (
        <div
          className="flex gap-1.5 overflow-x-auto px-3 py-2 bg-black/70"
          onClick={(e) => e.stopPropagation()}
        >
          {slides.map((s, si) => (
            <button
              key={si}
              type="button"
              onClick={() => setIdx(si)}
              className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 bg-black border-2 overflow-hidden relative ${si === idx ? 'border-brand-primary' : 'border-transparent opacity-70 hover:opacity-100'}`}
              data-testid={`provider-showcase-thumb-${si}`}
            >
              {s.kind === 'video' ? (
                <>
                  {s.poster ? (
                    <img src={fullUrl(s.poster)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <video src={fullUrl(s.url)} className="w-full h-full object-cover" preload="metadata" muted />
                  )}
                  <span className="absolute bottom-0 right-0 text-[8px] uppercase tracking-wider font-bold bg-black/70 text-sand-50 px-1">▶</span>
                </>
              ) : (
                <img src={fullUrl(s.url)} alt="" className="w-full h-full object-cover" />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Sticky bottom action bar */}
      {onToggleCart && (
        <div
          className="bg-white border-t-2 border-[#E8E1D5] px-4 py-3 flex items-center justify-between gap-3"
          onClick={(e) => e.stopPropagation()}
          data-testid="provider-showcase-action-bar"
        >
          <div className="min-w-0">
            {priceText && (
              <div className="font-heading text-lg sm:text-xl font-bold text-brand-primary truncate" data-testid="provider-showcase-price">
                {priceText}
              </div>
            )}
            {subPriceText && (
              <div className="text-[11px] text-text-secondary truncate">{subPriceText}</div>
            )}
          </div>
          <button
            type="button"
            onClick={() => { onToggleCart(); }}
            className={`flex items-center gap-1.5 px-5 py-2.5 text-sm font-bold uppercase tracking-wider text-sand-50 rounded-none flex-shrink-0 mr-28 sm:mr-44 ${inCart ? 'bg-red-500 hover:bg-red-600' : 'bg-brand-primary hover:bg-[#A84A38]'}`}
            data-testid="provider-showcase-toggle-cart"
          >
            {inCart ? <><X size={14} weight="bold" /> Remove from estimate</> : <><Plus size={14} weight="bold" /> Add to estimate</>}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProviderShowcaseLightbox;
